import { useCallback, useState } from 'react';
import { IconUpload } from '@picsart/design-system/foundation/icons/IconUpload';
import { useTranslator } from '@picsart/rc/services/localization';
import UploadSourceMenu from '@/components/UploadSourceMenu';
import sendUploadClickAnalytics from '@/utils/analytics/sendUploadClickAnalytics';
import useStyles from './styles';
import {
  getInvalidTitle,
  getInvalidHint,
} from '@/utils/upload/getUploadErrorMessages';
import type { DropZoneProps } from './types';

const DropZone = ({
  accept,
  title,
  allowedExtensions,
  hasError,
  hasUploadError,
  showInvalidState,
  isDragging,
  onSelectFromDrive,
  onSelectFromCanvas,
  onSelectFromNativePicker,
  hasCanvasImages,
  onOpenFilePicker,
  onClearInvalidFeedback,
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
}: DropZoneProps) => {
  const classes = useStyles({ hasError: hasError || hasUploadError });
  const translate = useTranslator();
  const [showSourceMenu, setShowSourceMenu] = useState(false);

  const handleClick = useCallback(() => {
    // Clear invalid state when user clicks to try again
    onClearInvalidFeedback();
    if (onSelectFromNativePicker) {
      sendUploadClickAnalytics();
      onSelectFromNativePicker();
      return;
    }
    if (onSelectFromDrive || hasCanvasImages) {
      setShowSourceMenu(true);
    } else {
      sendUploadClickAnalytics();
      onOpenFilePicker();
    }
  }, [
    onSelectFromNativePicker,
    onSelectFromDrive,
    hasCanvasImages,
    onOpenFilePicker,
    onClearInvalidFeedback,
  ]);

  const handleSelectFromDevice = useCallback(() => {
    sendUploadClickAnalytics();
    onOpenFilePicker();
  }, [onOpenFilePicker]);

  const handleSelectFromDrive = useCallback(() => {
    sendUploadClickAnalytics();
    onSelectFromDrive?.();
  }, [onSelectFromDrive]);

  const invalidTitle = getInvalidTitle({ hasUploadError, accept });
  const invalidHintText = getInvalidHint({ hasUploadError, allowedExtensions });

  const dropzoneElement = (
    <div
      className={`${classes.dropzone} ${
        showInvalidState
          ? classes.dropzoneInvalid
          : isDragging
          ? classes.dropzoneDragging
          : ''
      }`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      onDrop={(e) => {
        sendUploadClickAnalytics();
        onDrop(e);
      }}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      role="button"
      tabIndex={0}
    >
      <div className={classes.wrapper}>
        <div className={classes.iconWrapper}>
          <IconUpload fill="currentColor" className={classes.icon} />
        </div>
        <span className={classes.text}>
          {showInvalidState
            ? translate(invalidTitle)
            : translate(title || 'Add your image')}
        </span>
        <span className={classes.hint}>
          {showInvalidState
            ? translate(invalidHintText)
            : translate(
                onSelectFromDrive || hasCanvasImages
                  ? 'Upload from device, Drive, or Canvas'
                  : 'Drop file here or tap to upload'
              )}
        </span>
      </div>
    </div>
  );

  return (
    <div className={classes.dropzoneWrapper}>
      {onSelectFromDrive || hasCanvasImages ? (
        <UploadSourceMenu
          reference={dropzoneElement}
          isOpen={showSourceMenu}
          onClose={() => setShowSourceMenu(false)}
          onSelectFromDevice={handleSelectFromDevice}
          onSelectFromDrive={
            onSelectFromDrive ? handleSelectFromDrive : undefined
          }
          onSelectFromCanvas={onSelectFromCanvas}
          hasCanvasImages={hasCanvasImages}
        />
      ) : (
        dropzoneElement
      )}
    </div>
  );
};

export default DropZone;
